// src/belediye/components/tasks/GorevBits.js
// FAZ-28 — Kurum İçi Görevler ortak yapı taşları (kart, rozet, geri sayım).
//
// Kurallar (05 §7):
//   - Renk şeridi YALNIZ termin durumunu anlatır (backend is_overdue/is_due_soon
//     hesaplar — frontend tarih matematiği YAPMAZ); öncelik ayrı ince etikettir.
//   - Renk tek başına anlam taşımaz: ikon + metin her zaman eşlik eder.
//   - Kaynak rozeti altın zemin ("Başkan" / "Başkan Yrd." / "Yönetim").

import React from "react";
import {
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import "./gorev.css";

// ── Türkçe insan-dili geri sayım (backend bayrakları + tarih SADECE metin için) ──
export function dueText(task) {
  if (!task?.due_date) return null;
  if (task.status === "tamamlandi") return "tamamlandı";
  if (task.status === "iptal_edildi") return "iptal edildi";
  // FAZ-47: iş teslim edildi, sıra makamda — termin baskısı yazma
  // (backend is_overdue de bu durumda False döner).
  if (task.status === "onay_bekliyor") return "onay bekliyor";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${task.due_date}T00:00:00`);
  const days = Math.round((due - today) / 86400000);
  if (task.is_overdue) return days === -1 ? "1 gün gecikti" : `${Math.abs(days)} gün gecikti`;
  if (days === 0) return "bugün son gün";
  if (days === 1) return "yarın son";
  return `${days} gün var`;
}

export function stripClass(task) {
  if (task?.status === "tamamlandi") return "gk--done";
  if (task?.status === "onay_bekliyor") return "gk--pending";
  if (task?.is_overdue) return "gk--overdue";
  if (task?.is_due_soon) return "gk--due";
  return "gk--normal";
}

export function SourceBadge({ task }) {
  return <span className="gk-source">{task?.source_label_display || "Yönetim"}</span>;
}

export function DueChip({ task }) {
  const text = dueText(task);
  if (!text) return null;
  const cls = stripClass(task);
  const Icon =
    cls === "gk--overdue" ? ExclamationTriangleIcon : cls === "gk--done" ? CheckCircleIcon : ClockIcon;
  const saat = task?.due_time ? ` · ${String(task.due_time).slice(0, 5)}` : "";
  return (
    <span className={`gk-due ${cls}`} title={`${task.due_date}${saat}`}>
      <Icon className="gk-ic" /> {text}{saat}
    </span>
  );
}

// FAZ-55/2 — GÖREV DURUMU YAZILARI, kullanıcının diliyle.
// Kullanıcı kararı (2026-07-27): görevin durumu ekranda "yeni / başlandı /
// onay bekliyor / kapandı" diye okunsun. Yalnız GÖRÜNEN yazı değişir; status
// kodları ve DB değerleri aynı kalır (backend get_status_display hâlâ resmî
// etiketi döner — rapor/dışa aktarım etkilenmez).
// Tek yer: her ekran StatusChip/statusLabel üzerinden okuduğu için liste,
// detay ve sohbet aynı dili konuşur.
export const STATUS_ETIKETLERI = {
  yeni: "Yeni",
  devam_ediyor: "Başlandı",
  geri_bildirim_bekliyor: "Geri bildirim bekliyor",
  onay_bekliyor: "Onay bekliyor",
  tamamlandi: "Kapandı",
  iptal_edildi: "İptal",
};

export function statusLabel(task) {
  return STATUS_ETIKETLERI[task?.status] || task?.status_display || task?.status || "";
}

// FAZ-55/4 — DURUM = RENK. Kullanıcı kararı (2026-07-27): "her bir statüye bir
// renk ata … hangi statüdeyse şerit ve açılan hali o renkte olsun, direkt
// renkten belli olsun."
// Buradaki sınıf yalnız bir KAPI açar; gerçek renkler CSS'te tek yerde
// (--st-cizgi / --st-zemin / --st-kenar / --st-yazi) tanımlıdır. Böylece şerit,
// açılan alan ve içindeki durum çipi TEK tondan beslenir — biri değişince
// hepsi değişir.
// Renk TEK BAŞINA anlam taşımaz: durum yazısı (statusLabel) her zaman yanında.
export const STATUS_TONU = {
  yeni: "st--yeni", // nötr gri — henüz kimse dokunmadı
  devam_ediyor: "st--basladi", // mavi — iş yürüyor
  geri_bildirim_bekliyor: "st--geri", // amber — top karşı tarafta
  onay_bekliyor: "st--onay", // mor — iş bitti, sıra makamda
  tamamlandi: "st--kapandi", // yeşil — kapandı
  iptal_edildi: "st--iptal", // kırmızı — iptal
};

export function statusTone(task) {
  return STATUS_TONU[task?.status] || "st--yeni";
}

export function StatusChip({ task }) {
  return (
    <span className={`gk-status gk-status--${task?.status || "yeni"}`}>
      {statusLabel(task)}
    </span>
  );
}

export function AssigneeSummary({ task }) {
  const list = Array.isArray(task?.assignees) ? task.assignees : [];
  if (!list.length) return null;
  const first = list.slice(0, 2).map((a) => a.display).join(", ");
  const more = list.length > 2 ? ` +${list.length - 2}` : "";
  return <span className="gk-assignees">{first}{more}</span>;
}

// ── Görev kartı — Görev Yönetimi + Görevlerim + saha sekmesi ortak ─────
export function TaskCard({ task, onOpen, children }) {
  return (
    <div
      className={`gk-card ${stripClass(task)}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen && onOpen(task)}
      onKeyDown={(e) => e.key === "Enter" && onOpen && onOpen(task)}
    >
      <div className="gk-card-main">
        <div className="gk-card-top">
          <SourceBadge task={task} />
          {task.priority && task.priority !== "normal" && (
            <span className={`gk-priority gk-priority--${task.priority}`}>
              {task.priority_display}
            </span>
          )}
          <DueChip task={task} />
        </div>
        <div className="gk-title">{task.title}</div>
        <div className="gk-meta">
          {task.unit_name && <span>{task.unit_name}</span>}
          <AssigneeSummary task={task} />
          <StatusChip task={task} />
        </div>
        {task.last_comment?.body ? (
          <div className="gk-lastcomment">
            <ChatBubbleLeftRightIcon className="gk-ic" />
            <span>{task.last_comment.body}</span>
          </div>
        ) : null}
      </div>
      {children ? (
        <div className="gk-card-actions" onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

// ── Sayaç şeridi (tıklanabilir filtre çipleri) ─────────────────────────
export function CountersStrip({ counters, active, onPick }) {
  if (!counters) return null;
  const items = [
    { key: "acik", label: "Açık", cls: "" },
    { key: "yaklasan", label: "Yaklaşan", cls: "gk--due" },
    { key: "geciken", label: "Geciken", cls: "gk--overdue" },
    { key: "tamamlanan_7g", label: "Tamamlanan (7g)", cls: "gk--done" },
  ];
  return (
    <div className="gk-counters">
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          className={`gk-counter ${it.cls} ${active === it.key ? "is-active" : ""}`}
          onClick={() => onPick && onPick(it.key)}
        >
          <b>{counters[it.key] ?? 0}</b> {it.label}
        </button>
      ))}
    </div>
  );
}

// ── "Ne durumdayız?" hazır soruları (05 §5; admin yönetimi v2) ─────────
export const PRESET_QUESTIONS = [
  "Son durum nedir?",
  "Bugün tamamlanır mı?",
  "Yerinde inceleme yapıldı mı?",
  "Fotoğraf paylaşabilir misiniz?",
  "Bir engel var mı?",
  "Süre güncellensin mi?",
];

// ── ATANAN kalıp cevapları (C2): atanan "ne durumdayız" SORMAZ, durum BİLDİRİR.
// Atayanın PRESET_QUESTIONS setinden ayrı; göreve YORUM olarak düşer.
export const ASSIGNEE_PRESETS = [
  "İşe başladım.",
  "Üzerinde çalışıyorum.",
  "Bugün tamamlanır.",
  "Yerinde inceledim.",
  "Bir engel var, görüşelim.",
  "Ek süre gerekli.",
  "Tamamlandı, kontrol edebilirsiniz.",
];

export function EmptyState({ children }) {
  return <div className="gk-empty">{children}</div>;
}
